import { defineStore } from 'pinia'
import { useStorage } from '@vueuse/core'
import { EOrganizationType } from '~/services/MSAPI/user/organization/types.js'
import type { NLogin } from '~/services/MSAPI/user/authentication/types'
import type { NGetSession } from '~/services/MSAPI/user/user/types'
import { EPlanType } from '~/constants/organizationPlan'
import type { NGetOrganizationPlanFeatures } from '~/services/MSAPI/balance/organizationPlans/types'
import type { IPermission, SalesManager } from '~/services/MSAPI/user/types'
import UserService from '~/services/MSAPI/user'

// TODO: AVALIAR DADOS SENSÍVEIS NO LOCALSTORAGE

export interface IUserData {
  name?: string | null
  userId?: string | null
  email?: string | null
  language?: number | null
  createdDateTime?: string | null
  dateFormat?: string | null
  timeZone?: string | null
  permissions?: IPermission[] | null
  salesManager?: SalesManager | null
}

export interface IOrganizationData {
  id?: string | null
  name?: string | null
  organizationBalanceConfigType?: number | null
  organizationBaseId?: string | null
  organizationStatus?: number | null
  organizationType?: number | null | undefined
  planName?: string | null
  planType?: EPlanType | null
  planId?: string | null
  planFeatures?: NGetOrganizationPlanFeatures.IResponseData | null
}

export interface IAlertData {
  alerts?: NGetSession.IAlert[] | null
}

export interface ISetUserSessionOptions {
  updateLanguage?: boolean
}

const emptyUserData: IUserData = {
  name: null,
  userId: null,
}

export const useAuthStore = defineStore('auth', () => {
  const accessTokenKey = 'accessToken'
  const refreshTokenKey = 'refreshToken'
  const userDataKey = 'userData'
  const organizationDataKey = 'organizationDataKey'
  const alertDataKey = 'alertDataKey'

  const accessToken = useStorage<string | null>(
    accessTokenKey,
    localStorage.getItem(accessTokenKey),
  )

  const refreshToken = useStorage<string | null>(
    refreshTokenKey,
    localStorage.getItem(refreshTokenKey),
  )

  const storedUserData = localStorage.getItem(userDataKey)

  const initialUserData: IUserData = storedUserData
    ? JSON.parse(storedUserData)
    : emptyUserData

  const initialOrganizationData: IOrganizationData = {
    name: null,
    organizationBalanceConfigType: null,
    organizationBaseId: null,
    organizationStatus: null,
    organizationType: null,
    planName: null,
    planType: null,
    planId: null,
  }

  const initiAlertData: IAlertData = {
    alerts: [],
  }

  const userData = useStorage<IUserData>(userDataKey, initialUserData)

  const organizationData = useStorage<IOrganizationData>(
    organizationDataKey,
    initialOrganizationData,
  )

  const alertData = useStorage<IAlertData>(alertDataKey, initiAlertData)

  const isUserAuthenticated = computed(() =>
    Boolean(accessToken.value && refreshToken.value && userData.value?.userId),
  )

  function logout() {
    accessToken.value = null
    refreshToken.value = null
    userData.value = emptyUserData
    organizationData.value = initialOrganizationData
    useWhitelabelStore().refreshWhitelabel()
    return navigateTo({ name: 'auth-login' })
  }

  function setAccessToken(value: string) {
    accessToken.value = value
  }

  function setRefreshToken(value: string) {
    refreshToken.value = value
  }

  function setUserData(value: NLogin.IResponseData) {
    userData.value = {
      userId: value.id,
      name: value.name,
      email: value.email,
      dateFormat: value.dateTimeFormat,
      timeZone: value.timeZone,
    }
  }

  function updateUserEmail(email: string) {
    userData.value.email = email
  }

  function setUserSession(value: IUserData, options?: ISetUserSessionOptions) {
    userData.value.createdDateTime = value.createdDateTime
    userData.value.name = value.name
    userData.value.email = value.email
    userData.value.language = value.language
    userData.value.salesManager = value.salesManager

    // * TODO: Pedir ao backend (GetSession) para que retorne timeZone e dateTimeFormat
    if (value.timeZone) userData.value.timeZone = value.timeZone

    if (value.dateFormat) userData.value.dateFormat = value.dateFormat

    if ((options?.updateLanguage ?? true) && value.language) {
      const languageStore = useLanguageStore()
      languageStore.setUserLanguage(value.language)
    }
  }

  function setUserPermissions(permissions: IPermission[]) {
    userData.value.permissions = permissions
  }

  function setUserSalesManager(salesManager: SalesManager) {
    userData.value.salesManager = salesManager
  }

  function setAlerts(alerts: NGetSession.IAlert[]) {
    alertData.value.alerts = alerts
  }

  function setOrganizationSession(value: NGetSession.IOrganization) {
    organizationData.value = {
      name: value.name,
      organizationBalanceConfigType: value.organizationBalanceConfigType,
      organizationBaseId: value.organizationBaseId,
      organizationStatus: value.organizationStatus,
      organizationType: value.organizationType,
    }
  }

  function setOrganizationPlanSession(
    planName: string | null,
    planType: number | null,
    planId: string | null,
  ) {
    organizationData.value.planName = planName
    organizationData.value.planType = planType
    organizationData.value.planId = planId
  }

  function setOrganizationPlanFeatures(
    planFeatures: NGetOrganizationPlanFeatures.IResponseData,
  ) {
    organizationData.value.planFeatures = planFeatures
  }

  function resetOrganizationData() {
    organizationData.value = initialOrganizationData
  }

  const isOrganizationPlanFree = computed(() => {
    // TODO: Corrigir quando remover o plano TRIAL
    return organizationData.value.planType === EPlanType.TRIAL
  })

  const isSenditUser = computed(
    () => organizationData.value.organizationType === EOrganizationType.SENDIT,
  )

  const isOrganizationBase = (organizationId: string) =>
    organizationData.value.organizationBaseId === organizationId

  const isRenewingToken = ref(false)
  let renewTokenPromise: Promise<void> | null = null

  const setRenewingToken = (value: boolean) => {
    isRenewingToken.value = value
    renewTokenPromise = null
  }

  const renewToken = (): Promise<void> | void => {
    if (isRenewingToken.value) {
      return renewTokenPromise || undefined
    }
    setRenewingToken(true)

    renewTokenPromise = new Promise<void>((resolve, reject) => {
      if (refreshToken.value) {
        new UserService().authentication
          .renewToken(refreshToken.value)
          .then((result: any) => {
            setAccessToken(result.data!.accessToken)
            setRefreshToken(result.data!.refreshToken)
            resolve()
          })
          .catch(() => {
            reject()
          })
          .finally(() => {
            setRenewingToken(false)
          })
      } else {
        logout()
        setRenewingToken(false)
        reject()
      }
    })
    return renewTokenPromise
  }

  return {
    accessToken,
    refreshToken,
    userData,
    organizationData,
    alertData,
    isUserAuthenticated,
    isSenditUser,
    isOrganizationPlanFree,
    isRenewingToken,
    renewTokenPromise,
    renewToken,
    setAccessToken,
    setRefreshToken,
    setUserData,
    updateUserEmail,
    setUserSession,
    setUserPermissions,
    setAlerts,
    setUserSalesManager,
    setOrganizationSession,
    setOrganizationPlanSession,
    setOrganizationPlanFeatures,
    logout,
    resetOrganizationData,
    isOrganizationBase,
  }
})
